import "./App.css";
import "./mobile.css";
import { Suspense, lazy } from "react";
import { BrowserRouter, Routes, Route, Navigate, useOutletContext } from "react-router-dom";
import { usePageAnalytics } from "@/hooks/usePageAnalytics";
import usePageTracking from "@/hooks/usePageTracking";
import useAnalyticsPageTracking from "@/hooks/useAnalyticsPageTracking";
import { AuthProvider } from "@/contexts/AuthContext";
import CookieConsent from "./components/CookieConsent";
import { Toaster } from "./components/ui/sonner";

// Core Layout & Pages
import Layout from "./components/Layout";
import HomePage from "./pages/home/HomePage";

// Auxiliary Pages

// Pages that are not SEO landing pages load as separate chunks. Home, the brand pages and the directory stay
// in the main bundle on purpose: their server-rendered HTML must not be blanked while a chunk loads.
const ForBusinessesPage = lazy(() => import("./pages/business/ForBusinessesPage"));
const BusinessPricingPage = lazy(() => import("./pages/business/BusinessPricingPage"));
const BusinessGetStartedPage = lazy(() => import("./pages/business/BusinessGetStartedPage"));
const BusinessFeaturesPage = lazy(() => import("./pages/business/BusinessFeaturesPage"));
const About = lazy(() => import("./pages/about/About"));
const TrustIssue = lazy(() => import("./pages/trust/TrustIssue"));
const BadgeProblemPage = lazy(() => import("./pages/badge/BadgeProblemPage"));
const BadgeInfoPlaceholder = lazy(() => import("./pages/badge/BadgeInfoPlaceholder"));
const ArticlesPage = lazy(() => import("./pages/trust/ArticlesPage"));
const ProductPage = lazy(() => import("./pages/brands/ProductPage"));
const PrivacyPolicy = lazy(() => import("./pages/legal/PrivacyPolicy"));
const TermsAndConditions = lazy(() => import("./pages/legal/TermsAndConditions"));
const AuthCallback = lazy(() => import("./pages/auth/AuthCallback"));
const Profile = lazy(() => import("./pages/profile/Profile"));
const ProfileSettings = lazy(() => import("./pages/profile/ProfileSettings"));
const NotFoundPage = lazy(() => import("./pages/NotFound/NotFoundPage"));

// SEO landing routes (brand page, brand explorer, directory) are code-split too, but they are preloaded
// BEFORE the first render when they are the page being opened (see preloadInitialRoute below). The
// server-rendered HTML therefore stays on screen until the app can replace it with the same content.
function lazyRoute(factory) {
  const Lazy = lazy(factory);
  let Loaded = null;
  function RouteComponent(props) {
    return Loaded ? <Loaded {...props} /> : <Lazy {...props} />;
  }
  RouteComponent.preload = () =>
    factory().then((module) => {
      Loaded = module.default;
    });
  return RouteComponent;
}

const BrandsPage = lazyRoute(() => import("./pages/brands/BrandsPage"));
const BrandPage = lazyRoute(() => import("./pages/brands/BrandPage"));
const DirectoryPage = lazyRoute(() => import("./pages/brands/DirectoryPage"));

export function preloadInitialRoute() {
  const path = window.location.pathname.replace(/\/+$/, "") || "/";
  let load = null;
  if (path === "/brands") load = BrandsPage.preload;
  else if (/^\/brands\/(page|category)\//.test(path)) load = DirectoryPage.preload;
  else if (/^\/brands\/[^/]+$/.test(path)) load = BrandPage.preload;
  if (!load) return Promise.resolve();
  // Never hold the first render hostage to a slow or failed chunk.
  return Promise.race([load().catch(() => {}), new Promise((resolve) => setTimeout(resolve, 4000))]);
}

function PageFallback() {
  return (
    <div className="min-h-[60vh] grid place-items-center" role="status" aria-label="Loading">
      <div className="w-8 h-8 rounded-full border-2 border-brand-emerald border-t-transparent animate-spin" />
    </div>
  );
}

const withSuspense = (element) => <Suspense fallback={<PageFallback />}>{element}</Suspense>;

function HomePageRoute() {
  const { isAnimationComplete, onSearch, onNavigate } = useOutletContext();
  return <HomePage isAnimationComplete={isAnimationComplete} onSearch={onSearch} onNavigate={onNavigate} />;
}

function BrandsPageRoute() {
  const {
    brandSearchQuery,
    setBrandSearchQuery,
    brandCategory,
    setBrandCategory,
    brandOnlyVerified,
    setBrandOnlyVerified,
    heroSearchInputRef,
    onNavigate,
  } = useOutletContext();

  return (
    <BrandsPage
      initialQuery={brandSearchQuery}
      searchTerm={brandSearchQuery}
      onSearchTermChange={setBrandSearchQuery}
      selectedCategory={brandCategory}
      onCategoryChange={setBrandCategory}
      onlyVerified={brandOnlyVerified}
      onOnlyVerifiedChange={setBrandOnlyVerified}
      heroSearchInputRef={heroSearchInputRef}
      onNavigate={onNavigate}
    />
  );
}

function AnalyticsListener() {
  usePageAnalytics();
  usePageTracking();
  useAnalyticsPageTracking();
  return null;
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AnalyticsListener />
        <Routes>
          {/* Shared Layout & Core Routes */}
          <Route element={<Layout />}>
            <Route path="/" element={<HomePageRoute />} />
            <Route path="/brands" element={withSuspense(<BrandsPageRoute />)} />
            <Route path="/the-badge/the-problem" element={withSuspense(<BadgeProblemPage />)} />
            <Route path="/the-badge/what-we-do" element={<Navigate to="/the-badge/things-to-know" replace />} />
            <Route path="/the-badge/things-to-know" element={withSuspense(<BadgeInfoPlaceholder />)} />
            <Route path="/the-badge" element={<Navigate to="/the-badge/things-to-know" replace />} />
            <Route path="/how-it-works" element={<Navigate to="/the-badge/things-to-know" replace />} />
            {/* Auxiliary routes inside layout */}
            <Route path="/about" element={withSuspense(<About />)} />
            <Route path="/about/" element={withSuspense(<About />)} />
            <Route path="/trust-issue" element={withSuspense(<TrustIssue />)} />
            <Route path="/trust-issue/" element={withSuspense(<TrustIssue />)} />
            <Route path="/trust-issue/articles" element={withSuspense(<ArticlesPage />)} />
            <Route path="/trust-issue/articles/" element={withSuspense(<ArticlesPage />)} />
            <Route path="/the-problem" element={<Navigate to="/the-badge/the-problem" replace />} />
            <Route path="/the-problem/" element={<Navigate to="/the-badge/the-problem" replace />} />
            {/* /brand/:id and /brands/unverified/:slug 301 to /brands/:slug at the edge (functions/). */}
            <Route path="/brands/page/:page" element={withSuspense(<DirectoryPage />)} />
            <Route path="/brands/category/:cat" element={withSuspense(<DirectoryPage />)} />
            <Route path="/brands/category/:cat/page/:page" element={withSuspense(<DirectoryPage />)} />
            <Route path="/brands/:brandId" element={withSuspense(<BrandPage />)} />
            <Route path="/brands/:brandId/products/:productId" element={withSuspense(<ProductPage />)} />
            <Route path="/privacy" element={withSuspense(<PrivacyPolicy />)} />
            <Route path="/terms" element={withSuspense(<TermsAndConditions />)} />
            <Route path="/profile" element={withSuspense(<Profile />)} />
            <Route path="/profile/settings" element={withSuspense(<ProfileSettings />)} />
            <Route path="/profile/settings/" element={withSuspense(<ProfileSettings />)} />
            {/* Unknown paths get a 404 page instead of redirecting to the homepage. */}
            <Route path="*" element={withSuspense(<NotFoundPage />)} />
          </Route>

          {/* Standalone business experience — intentionally outside the consumer layout */}
          <Route path="/business" element={withSuspense(<ForBusinessesPage />)} />
          <Route path="/business/pricing" element={withSuspense(<BusinessPricingPage />)} />
          <Route path="/business/get-started" element={withSuspense(<BusinessGetStartedPage />)} />
          <Route path="/business/features" element={withSuspense(<BusinessFeaturesPage />)} />
          <Route path="/for-businesses" element={<Navigate to="/business" replace />} />
          <Route path="/for-business" element={<Navigate to="/business" replace />} />
          <Route path="/for-business/" element={<Navigate to="/business" replace />} />

          <Route path="/auth/callback" element={withSuspense(<AuthCallback />)} />
        </Routes>
        <CookieConsent />
        <Toaster position="top-center" />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
